import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import DateTime from '../../../../utils/DateTime';

const useStyles = makeStyles(theme => ({
  dialogHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.75),
  },
  iconCircle: {
    flex: 'none',
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#F2FBF7',
    border: '1px solid #CFEADD',
    color: '#0F8461',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    margin: 0,
    fontSize: 21,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: '#16141D',
  },
  subtitle: {
    margin: theme.spacing(0.875, 0, 0),
    fontSize: 13.8,
    lineHeight: 1.55,
    color: '#5F5D75',
  },
  subtitleStrong: {
    color: '#16141D',
    fontWeight: 600,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: '#8A88A0',
  },
  optionButton: {
    appearance: 'none' as const,
    width: '100%',
    textAlign: 'left' as const,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.875, 2),
    border: '1.5px solid #E6E4EF',
    borderRadius: 12,
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'border-color .15s ease, background .15s ease',
    fontFamily: 'inherit',
    '&:hover': {
      borderColor: '#BFE0D3',
    },
  },
  optionButtonSelected: {
    borderColor: '#0F8461',
    backgroundColor: '#F2FBF7',
    '&:hover': {
      borderColor: '#0F8461',
    },
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid #CDCADF',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    transition: 'all .15s ease',
    marginTop: 2,
  },
  radioSelected: {
    borderColor: '#0F8461',
    backgroundColor: '#0F8461',
  },
  radioDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: 'transparent',
  },
  radioDotSelected: {
    backgroundColor: '#fff',
  },
  optionTitle: {
    display: 'block',
    fontSize: 14.5,
    fontWeight: 600,
    color: '#16141D',
  },
  optionDesc: {
    display: 'block',
    marginTop: 2,
    fontSize: 12.8,
    lineHeight: 1.45,
    color: '#73718A',
  },
  keepButton: {
    border: 'none',
    background: 'none',
    padding: theme.spacing(0.75, 0.25),
    color: '#3C3A50',
    fontSize: 13.5,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textTransform: 'none' as const,
    '&:hover': {
      color: '#16141D',
      textDecoration: 'underline',
      textUnderlineOffset: 3,
      backgroundColor: 'transparent',
    },
  },
  confirmButton: {
    height: 44,
    padding: theme.spacing(0, 2.75),
    border: 'none',
    borderRadius: '10px',
    backgroundColor: '#0F8461',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none' as const,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#0B6B4E',
    },
    '&:disabled': {
      backgroundColor: '#0F8461',
      color: '#fff',
      opacity: 0.5,
    },
  },
  cancelButton: {
    height: 44,
    padding: theme.spacing(0, 2.5),
    border: '1.5px solid #D7DBE0',
    borderRadius: '10px',
    backgroundColor: '#fff',
    color: '#16141D',
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none' as const,
    '&:hover': {
      borderColor: '#B6BCC4',
      backgroundColor: '#F7F7FB',
    },
  },
  infoGrid: {
    marginTop: theme.spacing(2.5),
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
    backgroundColor: '#ECEAF4',
    border: '1px solid #E6E4EF',
    borderRadius: 12,
    overflow: 'hidden',
  },
  infoCell: {
    backgroundColor: '#FBFBFD',
    padding: theme.spacing(1.625, 1.875),
  },
  infoLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: '#8A88A0',
  },
  infoValue: {
    display: 'block',
    marginTop: 3,
    fontSize: 14.5,
    fontWeight: 600,
    color: '#16141D',
  },
}));

const RenewIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

type TurnOffChoice = 'off' | 'change';

export const ToggleRenewalConfirmDialog = (
  {
    productName,
    autoRenew,
    open,
    onClose,
    toggleAutoRenew,
    onCancel,
    onConfirm,
    expiryDate,
    billingCycleLabel,
  }: {
    productName: string;
    autoRenew: boolean;
    open: boolean;
    onClose: () => void;
    toggleAutoRenew: () => void;
    onCancel?: () => void;
    onConfirm?: () => void;
    expiryDate?: Date;
    billingCycleLabel?: string;
  },
) => {
  const classes = useStyles();
  const intl = useIntl();
  const [choice, setChoice] = useState<TurnOffChoice | null>(null);

  useEffect(() => {
    if (open) {
      setChoice(null);
    }
  }, [open]);

  const productLabel = intl.formatMessage({ id: `feature.${productName}.product.name` });
  const dateLabel = expiryDate
    ? DateTime.formatDate(intl, expiryDate)
    : intl.formatMessage({ id: 'page.subscription.v3.renewal.dialog.date.fallback' });

  const handleDismiss = () => {
    onCancel?.();
    onClose();
  };

  const handleConfirm = () => {
    onConfirm?.();
    toggleAutoRenew();
  };

  const renderOption = (value: TurnOffChoice, titleId: string, descId: string) => {
    const selected = choice === value;
    return (
      <button
        type="button"
        className={`${classes.optionButton} ${selected ? classes.optionButtonSelected : ''}`}
        onClick={() => setChoice(value)}
      >
        <span className={`${classes.radio} ${selected ? classes.radioSelected : ''}`}>
          <span className={`${classes.radioDot} ${selected ? classes.radioDotSelected : ''}`} />
        </span>
        <span>
          <span className={classes.optionTitle}>
            <FormattedMessage id={titleId} />
          </span>
          <span className={classes.optionDesc}>
            <FormattedMessage id={descId} values={{ date: dateLabel }} />
          </span>
        </span>
      </button>
    );
  };

  // Turn OFF — "Before you turn off auto-renewal" survey-style dialog
  const renderTurnOffContent = () => (
    <>
      <Box className={classes.dialogHeader}>
        <span className={classes.iconCircle}><RenewIcon /></span>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h2" className={classes.title}>
            <FormattedMessage id="page.subscription.v3.renewal.dialog.off.title" />
          </Typography>
          <Typography className={classes.subtitle}>
            <FormattedMessage
              id="page.subscription.v3.renewal.dialog.off.subtitle"
              values={{
                productName: (
                  <strong className={classes.subtitleStrong}>{productLabel}</strong>
                ),
                date: dateLabel,
              }}
            />
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2.75, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography className={classes.sectionLabel}>
          <FormattedMessage id="page.subscription.v3.renewal.dialog.off.which" />
        </Typography>
        {renderOption(
          'off',
          'page.subscription.v3.renewal.dialog.off.option.justoff.title',
          'page.subscription.v3.renewal.dialog.off.option.justoff.desc',
        )}
        {renderOption(
          'change',
          'page.subscription.v3.renewal.dialog.off.option.change.title',
          'page.subscription.v3.renewal.dialog.off.option.change.desc',
        )}
      </Box>

      <Box
        sx={{
          mt: 3.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button disableRipple className={classes.keepButton} onClick={handleDismiss}>
          <FormattedMessage id="page.subscription.v3.renewal.dialog.off.keep.btn" />
        </Button>
        <Button
          className={classes.confirmButton}
          disabled={choice === null}
          onClick={handleConfirm}
        >
          <FormattedMessage id="page.subscription.v3.renewal.dialog.off.confirm.btn" />
        </Button>
      </Box>
    </>
  );

  // Turn ON — confirmation dialog with renewal facts
  const renderTurnOnContent = () => (
    <>
      <Box className={classes.dialogHeader}>
        <span className={classes.iconCircle}><RenewIcon /></span>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h2" className={classes.title}>
            <FormattedMessage id="page.subscription.v3.renewal.dialog.on.title" />
          </Typography>
          <Typography className={classes.subtitle}>
            <FormattedMessage
              id="page.subscription.v3.renewal.dialog.on.desc"
              values={{
                productName: (
                  <strong className={classes.subtitleStrong}>{productLabel}</strong>
                ),
              }}
            />
          </Typography>
        </Box>
        <IconButton
          aria-label={intl.formatMessage({ id: 'page.subscription.v3.renewal.dialog.on.cancel.btn' })}
          onClick={handleDismiss}
          sx={{ flex: 'none', width: 32, height: 32, borderRadius: '8px', color: '#8A88A0' }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {(expiryDate || billingCycleLabel) && (
        <Box className={classes.infoGrid}>
          <Box className={classes.infoCell}>
            <span className={classes.infoLabel}>
              <FormattedMessage id="page.subscription.v3.renewal.dialog.on.renews.label" />
            </span>
            <span className={classes.infoValue}>{dateLabel}</span>
          </Box>
          <Box className={classes.infoCell}>
            <span className={classes.infoLabel}>
              <FormattedMessage id="page.subscription.v3.renewal.dialog.on.cycle.label" />
            </span>
            <span className={classes.infoValue}>{billingCycleLabel ?? '—'}</span>
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25 }}>
        <Button className={classes.cancelButton} onClick={handleDismiss}>
          <FormattedMessage id="page.subscription.v3.renewal.dialog.on.cancel.btn" />
        </Button>
        <Button className={classes.confirmButton} onClick={handleConfirm}>
          <FormattedMessage id="page.subscription.v3.renewal.dialog.on.confirm.btn" />
        </Button>
      </Box>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: autoRenew ? 560 : 480,
          borderRadius: '20px',
          border: '1px solid #E6E4EF',
          padding: autoRenew ? '30px' : '28px',
          boxShadow: '0 24px 64px rgba(22,20,29,.18)',
        },
      }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(22,20,29,.42)' } } }}
    >
      {autoRenew ? renderTurnOffContent() : renderTurnOnContent()}
    </Dialog>
  );
};
